import { Button } from "@/components/ui/button/Button";
import { Input } from "@/components/ui/input/Input";
import { Label } from "@/components/ui/label/Label";
import { FormEvent, useState } from "react";
import styles from "./AssetBorrowerData.module.css";
import { cn } from "@/lib/utils";
import { useToast } from "@/lib/hooks/useToast";
import { useAssetBorrowingStore } from "@/store/asset_borrowing/assetBorrowingStore";
import { useAssetBorrowingNext } from "../AssetBorrowing";
import { useUserStore } from "@/store/user/userStore";

const MAX_UPLOAD_SIZE_BYTES = 1024 * 1024;
const FILE_TOO_LARGE_MESSAGE =
  "File too large. Maximum size is 1 MB.";

const AssetBorrowerData = () => {
  const store = useAssetBorrowingStore();
  const userStore = useUserStore();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const { setAssetBorrowingNext } = useAssetBorrowingNext();

  const shouldShowUpload =
  store.data.assets?.some(
    (a) =>
      a.name?.toLowerCase() === "panel" ||
      a.name?.toLowerCase() === "pedestal"
  ) ?? false;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAssetBorrowingNext(false);
    if (store.data.phone.trim() === "" || store.data.purpose.trim() === "" || (shouldShowUpload && !store.data.uploaded_file)) {
      toast({
        title: "Form error",
        description: "Please fill all required fields...",
      });

      return;
    } else if (isNaN(+store.data.phone)) {
      toast({
        title: "Form error",
        description: "Please fill phone number correctly...",
      });

      return;
    }
    setLoading(false);
    toast({
      title: "User data successfully saved.",
      description: "Please proceed to next step..",
    });
    setAssetBorrowingNext(true);
  };

  return (
    <form onSubmit={handleSubmit}>
      {shouldShowUpload && (
        <div className={styles.inputContainer}>
          <Label>
            Upload Document SSO (Max 1 MB){" "}
            <span className="text-red-500 text-lg">*</span>
          </Label>

          <input
            type="file"
            id="upload"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            className="hidden"
            aria-describedby={uploadError ? "upload-error" : undefined}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                if (file.size > MAX_UPLOAD_SIZE_BYTES) {
                  store.update("uploaded_file", null);
                  setUploadError(FILE_TOO_LARGE_MESSAGE);
                  e.target.value = "";
                  return;
                }

                setUploadError("");
                store.update("uploaded_file", file);
              }
            }}
          />

          <label
            htmlFor="upload"
            className={cn(
              "cursor-pointer flex items-center justify-center w-full border border-dashed rounded-lg py-3 transition",
              uploadError
                ? "border-red-500 bg-red-500/10 text-red-500 hover:bg-red-500/15"
                : "border-gray-400 hover:bg-gray-500",
            )}
          >
            Click to Upload File
          </label>

          {uploadError && (
            <div
              id="upload-error"
              role="alert"
              className="w-full rounded-md border border-red-500 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-500"
            >
              {uploadError}
            </div>
          )}

          {store.data.uploaded_file && (
            <p className="text-sm text-primary mt-2">
              Uploaded: <span className="font-medium">{store.data.uploaded_file.name}</span>
            </p>
          )}
        </div>
      )}
      
      <div className={styles.inputContainer}>
        <Label>Purpose <span className="text-red-500 text-lg">*</span></Label>
        <Input
          placeholder="Borrowing Purpose"
          name="purpose"
          id="purpose"
          value={store.data.purpose}
          onChange={(e) =>
            store.update("purpose", e.target.value)
          }
        />
      </div>
      <div className={styles.inputContainer}>
        <Label>Phone <span className="text-red-500 text-lg">*</span></Label>
        <Input
          placeholder="Phone Number"
          name="phone"
          id="phone"
          value={store.data.phone}
          onChange={(e) =>
            store.update("phone", e.target.value)
          }
        />
      </div>
      <div className={cn(styles.inputContainer, "pb-6")}>
        <div className="flex w-full gap-4 py-2 items-end">
          <div className="w-full">
            <Label className="text-lg text-gray-600">Leader</Label>
            <Input
              disabled={true}
              value={`${userStore.data?.Email}`}
            />
          </div>
        </div>      
      </div>
      <div className="pt-4">
        <Button type="submit" variant={"blue"} disabled={loading}>
          Save Data
        </Button>
      </div>
    </form>
  );
};

export default AssetBorrowerData;
